import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { auth } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import {
    listarComparaciones as listarComparacionesApi,
    guardarComparacion as guardarComparacionApi,
    eliminarComparacion as eliminarComparacionApi,
} from './api/comparaciones';
import {
    listarCatalogo, obtenerHistorialPrecios, obtenerEspecificaciones, resolverModeloActual,
} from './api/catalogo';
import Sidebar from './Sidebar';
import './ComparadorModelos.css';
import { BarChart3, Search, X, AlertCircle, CheckCircle, Save, Trash2, Bot, TrendingUp, BookOpen, Plus, FileDown } from 'lucide-react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const functions = getFunctions();
// timeout por defecto del SDK es 70s — la función hace historial+specs+usados
// de varios modelos en paralelo más una llamada a Claude con contexto grande,
// y el backend ya está configurado con timeoutSeconds: 300 — el cliente debe
// esperar acorde, si no corta la espera antes de que el servidor responda.
const comparadorPMFn = httpsCallable(functions, 'consultarComparadorPM', { timeout: 280000 });

// ── Colores ──────────────────────────────────────────────────
const MODEL_COLORS = ['#2563eb', '#0891b2', '#7c3aed', '#059669', '#dc2626', '#d97706'];

// ── Helpers ──────────────────────────────────────────────────
const clpFmt = n => Number.isFinite(Number(n))
    ? `$${new Intl.NumberFormat('es-CL').format(Number(n))}` : '—';

const precioNum = v => {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') return Number(v.replace(/[^0-9]/g, '')) || 0;
    return 0;
};

const normalizeStr = (s = '') =>
    String(s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

// ── Spec helpers ─────────────────────────────────────────────
const SPEC_SECTIONS = [
    { key: 'motor', label: '🔧 Motor y transmisión', fields: ['tipo_motor', 'cilindrada', 'potencia', 'par_motor', 'transmision', 'traccion', 'combustible'] },
    { key: 'dimensiones', label: '📐 Dimensiones', fields: ['largo', 'ancho', 'alto', 'distancia_entre_ejes', 'peso', 'capacidad_baul', 'capacidad_tanque'] },
    { key: 'suspension_frenos', label: '⚙️ Suspensión y frenos', fields: ['suspension_delantera', 'suspension_trasera', 'frenos_delanteros', 'frenos_traseros'] },
];
const getSpecValue = (specs, section, field) => {
    if (!specs) return null;
    const val = (specs.specs?.[section] || {})[field];
    return (val !== null && val !== undefined && val !== '') ? String(val) : null;
};
const getComparativaValue = (specs, subkey, field) => {
    if (!specs?.specs?.comparativas?.[subkey]) return null;
    const val = specs.specs.comparativas[subkey][field];
    return (val !== null && val !== undefined && val !== '') ? String(val) : null;
};

// ── Tabs config ──────────────────────────────────────────────
const TABS = [
    { key: 'comparar', label: 'Comparar', icon: <BarChart3 size={14} /> },
    { key: 'evolucion', label: 'Evolución', icon: <TrendingUp size={14} /> },
    { key: 'ia', label: 'Análisis PM', icon: <Bot size={14} /> },
    { key: 'guardadas', label: 'Guardadas', icon: <BookOpen size={14} /> },
];

// ── ReactMarkdown safe component ─────────────────────────────
function AIResponse({ text }) {
    return (
        <div className="comp-ai-box">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
function ComparadorModelos({ embedded = false, initialModels = null }) {
    const [activeTab, setActiveTab] = useState('comparar');

    // Búsqueda
    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
    const searchRef = useRef(null);
    const inputRef = useRef(null);
    const searchTimer = useRef(null);
    const catalogoRef = useRef(null); // cache del catálogo completo — evita refetch en cada búsqueda

    const obtenerCatalogoCompleto = async () => {
        if (!catalogoRef.current) {
            const { autos } = await listarCatalogo({ todos: true });
            catalogoRef.current = autos;
        }
        return catalogoRef.current;
    };

    // Modelos seleccionados
    const [selectedModels, setSelectedModels] = useState([]);

    // Datos por modelo
    const [specsMap, setSpecsMap] = useState({});
    const [specsLoading, setSpecsLoading] = useState({});
    const [preciosMap, setPreciosMap] = useState({});
    const [preciosLoading, setPreciosLoading] = useState({});
    const [historialMap, setHistorialMap] = useState({}); // { modelId: [{fecha, tipo, precio}] }
    const [histLoading, setHistLoading] = useState({});

    // Gráfico
    const [chartMode, setChartMode] = useState('absoluto'); // 'absoluto' | 'relativo'
    const [chartTipo, setChartTipo] = useState('todos');    // 'todos' | tipo específico

    // Guardadas
    const [savedList, setSavedList] = useState([]);
    const [savedLoading, setSavedLoading] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [saveName, setSaveName] = useState('');
    const [saving, setSaving] = useState(false);

    // IA
    const [aiPrompt, setAiPrompt] = useState('');
    const [aiResponse, setAiResponse] = useState('');
    const [aiLoading, setAiLoading] = useState(false);
    const [aiHistory, setAiHistory] = useState([]);

    // ── Dropdown click-outside ──────────────────────────────────
    useEffect(() => {
        const out = e => { if (searchRef.current && !searchRef.current.contains(e.target)) setDropdownOpen(false); };
        const repos = () => { if (dropdownOpen) calcPos(); };
        document.addEventListener('mousedown', out);
        window.addEventListener('scroll', repos, true);
        window.addEventListener('resize', repos);
        return () => {
            document.removeEventListener('mousedown', out);
            window.removeEventListener('scroll', repos, true);
            window.removeEventListener('resize', repos);
        };
    }, [dropdownOpen]);

    // ── Búsqueda debounce ───────────────────────────────────────
    useEffect(() => {
        if (!searchText.trim()) { setSearchResults([]); setDropdownOpen(false); return; }
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => buscar(searchText), 300);
        return () => clearTimeout(searchTimer.current);
    }, [searchText]);

    const calcPos = () => {
        if (!inputRef.current) return;
        const r = inputRef.current.getBoundingClientRect();
        setDropdownPos({ top: r.bottom + window.scrollY + 4, left: r.left + window.scrollX, width: r.width });
    };

    const buscar = async texto => {
        setSearchLoading(true); calcPos(); setDropdownOpen(true);
        try {
            const q = normalizeStr(texto);
            const catalogo = await obtenerCatalogoCompleto();
            const resultados = catalogo.filter(a => normalizeStr(`${a.marca} ${a.model} ${a.modelDetail}`).includes(q));
            setSearchResults(resultados.slice(0, 20));
        } catch (e) { console.error(e); setSearchResults([]); }
        finally { setSearchLoading(false); }
    };


    // ── Agregar / quitar modelo ─────────────────────────────────
    const agregar = modelo => {
        if (selectedModels.find(m => m.id === modelo.id) || selectedModels.length >= 6) return;
        setSelectedModels(p => [...p, modelo]);
        setSearchText(''); setDropdownOpen(false);
        cargarPrecios(modelo);
        cargarSpecs(modelo);
        cargarHistorial(modelo);
    };

    const quitar = id => {
        setSelectedModels(p => p.filter(m => m.id !== id));
        setPreciosMap(p => { const n = { ...p }; delete n[id]; return n; });
        setSpecsMap(p => { const n = { ...p }; delete n[id]; return n; });
        setHistorialMap(p => { const n = { ...p }; delete n[id]; return n; });
    };

    // ── Precarga opcional (ej. tab "Comparar" de Mis proyectos): arranca
    // con los autos ya elegidos, una sola vez al montar — después el
    // usuario agrega/quita libremente igual que en el comparador normal. ──
    const initialLoadDone = useRef(false);
    useEffect(() => {
        if (initialLoadDone.current || !initialModels?.length) return;
        initialLoadDone.current = true;
        const modelos = initialModels.slice(0, 6);
        setSelectedModels(modelos);
        modelos.forEach(modelo => {
            cargarPrecios(modelo);
            cargarSpecs(modelo);
            cargarHistorial(modelo);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialModels]);

    // ── Cargar precios actuales ─────────────────────────────────
    const cargarPrecios = async modelo => {
        setPreciosLoading(p => ({ ...p, [modelo.id]: true }));
        try {
            const directos = (modelo.tiposprecio || [])
                .map((tipo, i) => ({ tipo, precio: precioNum((modelo.precio || [])[i]) }))
                .filter(r => r.precio > 0);
            if (directos.length) { setPreciosMap(p => ({ ...p, [modelo.id]: directos })); return; }

            const actual = await resolverModeloActual(modelo.marca, modelo.model, modelo.modelDetail);
            if (actual) {
                const rows = (actual.tiposprecio || []).map((tipo, i) => ({ tipo, precio: precioNum((actual.precio || [])[i]) })).filter(r => r.precio > 0);
                setPreciosMap(p => ({ ...p, [modelo.id]: rows }));
            }
        } catch (e) { console.error(e); }
        finally { setPreciosLoading(p => ({ ...p, [modelo.id]: false })); }
    };

    // ── Cargar historial para gráfico ───────────────────────────
    const cargarHistorial = async modelo => {
        setHistLoading(p => ({ ...p, [modelo.id]: true }));
        try {
            const { historial: snapshots } = await obtenerHistorialPrecios(modelo.marca, modelo.model, modelo.modelDetail);
            const puntos = [];
            snapshots.forEach(data => {
                const fecha = new Date(data.date_add * 1000).toISOString().slice(0, 10);
                (data.tiposprecio || []).forEach((tipo, i) => {
                    const precio = precioNum((data.precio || [])[i]);
                    if (precio > 0) puntos.push({ fecha, tipo, precio, ts: data.date_add });
                });
            });
            // Dedup por fecha+tipo, quedarse con el más reciente
            const map = new Map();
            puntos.forEach(p => {
                const k = `${p.fecha}__${p.tipo}`;
                if (!map.has(k) || p.ts > map.get(k).ts) map.set(k, p);
            });
            setHistorialMap(prev => ({ ...prev, [modelo.id]: [...map.values()].sort((a, b) => a.ts - b.ts) }));
        } catch (e) { console.error(e); }
        finally { setHistLoading(p => ({ ...p, [modelo.id]: false })); }
    };

    // ── Cargar specs ────────────────────────────────────────────
    const cargarSpecs = async modelo => {
        setSpecsLoading(p => ({ ...p, [modelo.id]: true }));
        try {
            const specs = await obtenerEspecificaciones(modelo.marca.trim(), modelo.model.trim(), modelo.modelDetail.trim());
            setSpecsMap(p => ({ ...p, [modelo.id]: specs }));
        } catch (e) { setSpecsMap(p => ({ ...p, [modelo.id]: null })); }
        finally { setSpecsLoading(p => ({ ...p, [modelo.id]: false })); }
    };

    // ── Tipos de precio únicos ──────────────────────────────────
    const tiposPrecios = useMemo(() => {
        const all = new Set();
        selectedModels.forEach(m => (preciosMap[m.id] || []).forEach(r => all.add(r.tipo)));
        return [...all].sort();
    }, [selectedModels, preciosMap]);

    // ── Datos gráfico evolución ─────────────────────────────────
    const todosLosTipos = useMemo(() => {
        const s = new Set();
        selectedModels.forEach(m => (historialMap[m.id] || []).forEach(p => s.add(p.tipo)));
        return [...s].sort();
    }, [selectedModels, historialMap]);

    const chartData = useMemo(() => {
        const fechas = new Set();
        selectedModels.forEach(m => (historialMap[m.id] || []).forEach(p => { if (chartTipo === 'todos' || p.tipo === chartTipo) fechas.add(p.fecha); }));
        const sorted = [...fechas].sort();

        // Para modo relativo: precio base (primer dato) por modelo+tipo
        const bases = {};
        selectedModels.forEach(m => {
            const hist = (historialMap[m.id] || []).filter(p => chartTipo === 'todos' || p.tipo === chartTipo);
            if (!hist.length) return;
            const primerPrecio = hist[0].precio;
            bases[m.id] = primerPrecio;
        });

        return sorted.map(fecha => {
            const punto = { fecha };
            selectedModels.forEach((m, i) => {
                const hist = (historialMap[m.id] || []).filter(p => p.fecha === fecha && (chartTipo === 'todos' || p.tipo === chartTipo));
                if (!hist.length) return;
                // Si todos los tipos: usar el precio máximo del día (precio lista generalmente)
                const precio = chartTipo === 'todos' ? Math.max(...hist.map(h => h.precio)) : hist[0].precio;
                const key = `${m.marca} ${m.model}`;
                if (chartMode === 'relativo' && bases[m.id]) {
                    punto[key] = Number(((precio / bases[m.id] - 1) * 100).toFixed(2));
                } else {
                    punto[key] = precio;
                }
            });
            return punto;
        }).filter(p => Object.keys(p).length > 1);
    }, [selectedModels, historialMap, chartMode, chartTipo]);

    // ── Spec rows ───────────────────────────────────────────────
    const specRows = useMemo(() => {
        const rows = [];
        SPEC_SECTIONS.forEach(sec => {
            const fields = sec.fields.map(field => {
                const values = selectedModels.map(m => getSpecValue(specsMap[m.id], sec.key, field));
                return { field, values, hasAny: values.some(v => v !== null) };
            }).filter(f => f.hasAny);
            if (fields.length) {
                rows.push({ type: 'section', label: sec.label });
                fields.forEach(({ field, values }) => { const hasDiff = new Set(values.filter(Boolean)).size > 1; rows.push({ type: 'row', label: field, values, hasDiff }); });
            }
        });
        [['rendimiento', ['consumo_ciudad', 'consumo_carretera', 'consumo_mixto', 'aceleracion_0_100', 'velocidad_maxima'], '⛽ Rendimiento'],
        ['capacidades', ['pasajeros', 'carga_util'], '👥 Capacidades']].forEach(([sub, fields, label]) => {
            const valid = fields.map(f => ({ field: f, values: selectedModels.map(m => getComparativaValue(specsMap[m.id], sub, f)), hasAny: selectedModels.some(m => getComparativaValue(specsMap[m.id], sub, f) !== null) })).filter(f => f.hasAny);
            if (valid.length) { rows.push({ type: 'section', label }); valid.forEach(({ field, values }) => { rows.push({ type: 'row', label: field, values, hasDiff: new Set(values.filter(Boolean)).size > 1 }); }); }
        });
        return rows;
    }, [selectedModels, specsMap]);

    // ── Equip rows ──────────────────────────────────────────────
    const equipRows = useMemo(() => {
        const CAT_META = { comfort: { emoji: '🛋', label: 'Confort' }, exterior: { emoji: '🚗', label: 'Exterior' }, interior: { emoji: '🪑', label: 'Interior' }, seguridad: { emoji: '🛡', label: 'Seguridad' }, multimedia: { emoji: '📱', label: 'Multimedia' }, tecnologia: { emoji: '⚙️', label: 'Tecnología' }, adas: { emoji: '🤖', label: 'ADAS' } };
        const catMap = new Map();
        selectedModels.forEach(m => {
            const s = specsMap[m.id]?.specs;
            if (!s) return;
            ['equipamiento_estandar', 'equipamiento_diferencial'].forEach(tipo => {
                const eq = s[tipo]; if (!eq) return;
                Object.entries(eq).forEach(([cat, items]) => {
                    if (!Array.isArray(items)) return;
                    if (!catMap.has(cat)) catMap.set(cat, new Set());
                    items.forEach(item => { const name = typeof item === 'object' ? item.caracteristica : String(item); if (name) catMap.get(cat).add(name); });
                });
            });
        });
        if (!catMap.size) return [];
        const catOrder = Object.keys(CAT_META);
        const allCats = [...catOrder.filter(c => catMap.has(c)), ...[...catMap.keys()].filter(c => !catOrder.includes(c)).sort()];
        const rows = [];
        allCats.forEach(cat => {
            const meta = CAT_META[cat] || { emoji: '•', label: cat };
            rows.push({ type: 'equip-section', label: `${meta.emoji} ${meta.label}` });
            [...catMap.get(cat)].sort().forEach(nombre => {
                const values = selectedModels.map(m => {
                    const s = specsMap[m.id]?.specs; if (!s) return null;
                    for (const tipo of ['equipamiento_estandar', 'equipamiento_diferencial']) {
                        const eq = s[tipo]; if (!eq?.[cat]) continue;
                        const item = eq[cat].find(i => (typeof i === 'object' ? i.caracteristica : String(i)) === nombre);
                        if (item !== undefined) return typeof item === 'object' ? (item.valor ? 'SI' : 'NO') : 'SI';
                    }
                    return null;
                });
                if (!values.some(v => v !== null)) return;
                const hasDiff = new Set(values.filter(v => v !== null)).size > 1;
                rows.push({ type: 'equip-row', label: nombre, values, hasDiff });
            });
        });
        return rows;
    }, [selectedModels, specsMap]);

    const allLoading = selectedModels.some(m => preciosLoading[m.id] || specsLoading[m.id]);

    // ── Guardar comparación (Postgres/Neon) ──────────────────────
    const guardarComparacion = async () => {
        const user = auth.currentUser;
        if (!user || !selectedModels.length) return;
        setSaving(true);
        try {
            const nombre = saveName.trim() || `Comparación ${new Date().toLocaleDateString('es-CL')}`;
            await guardarComparacionApi(
                nombre,
                selectedModels.map(m => ({ marca: m.marca, model: m.model, modelDetail: m.modelDetail }))
            );
            setShowSaveModal(false); setSaveName('');
            await cargarGuardadas();
        } catch (e) { console.error(e); }
        finally { setSaving(false); }
    };

    const cargarGuardadas = async () => {
        const user = auth.currentUser;
        if (!user) return;
        setSavedLoading(true);
        try {
            setSavedList(await listarComparacionesApi());
        } catch (e) { console.error(e); }
        finally { setSavedLoading(false); }
    };

    const borrarGuardada = async id => {
        try { await eliminarComparacionApi(id); await cargarGuardadas(); }
        catch (e) { console.error(e); }
    };

    const cargarGuardadaEnComparador = async comp => {
        // Limpiar todo primero
        setPreciosMap({}); setSpecsMap({}); setHistorialMap({});

        const modelosCargados = [];

        // Resolver cada modelo guardado a su snapshot de precio más reciente
        for (const m of (comp.modelos || [])) {
            try {
                const actual = await resolverModeloActual(m.marca, m.model, m.modelDetail);
                modelosCargados.push(actual || { ...m, id: m.id || `${m.marca}_${m.model}_${m.modelDetail}` });
            } catch {
                modelosCargados.push({ ...m, id: m.id || `${m.marca}_${m.model}` });
            }
        }

        // Setear todos juntos — un solo render
        setSelectedModels(modelosCargados);

        // Cargar datos de cada modelo ahora que tenemos los objetos completos
        modelosCargados.forEach(modelo => {
            cargarPrecios(modelo);
            cargarSpecs(modelo);
            cargarHistorial(modelo);
        });

        setActiveTab('comparar');
    };

    useEffect(() => { if (activeTab === 'guardadas') cargarGuardadas(); }, [activeTab]);

    // ── Análisis IA ─────────────────────────────────────────────
    const analizarConIA = async (promptExtra = '') => {
        if (!selectedModels.length) return;
        setAiLoading(true);
        try {
            // Pasar modelos explícitamente — la función los usa para cargar datos reales
            const modelosPayload = selectedModels.map(m => ({
                marca: m.marca,
                model: m.model,
                modelDetail: m.modelDetail,
            }));
            const historialMensajes = aiHistory.map(h => ({ role: h.role, content: h.content }));
            const result = await comparadorPMFn({
                modelos: modelosPayload,
                prompt: promptExtra.trim(),   // vacío = análisis completo automático
                historial: historialMensajes,
                uid: auth.currentUser?.uid,
            });
            const respuesta = result.data?.response || '';
            setAiResponse(respuesta);
            const promptDisplay = promptExtra.trim() ||
                `Análisis completo: ${selectedModels.map(m => m.marca + ' ' + m.model).join(', ')}`;
            setAiHistory(p => [
                ...p,
                { role: 'user', content: promptDisplay },
                { role: 'assistant', content: respuesta },
            ]);
            setAiPrompt('');
        } catch (e) {
            console.error(e);
            setAiResponse('Error al conectar con el análisis PM. Verifica que la función esté desplegada.');
        }
        finally { setAiLoading(false); }
    };

    // ── Exportar informe en PDF ───────────────────────────────────
    // jsPDF con las fuentes estándar (Helvetica) sólo dibuja Latin-1/CP1252 —
    // emoji, viñetas Unicode, comillas tipográficas y guiones largos salen
    // como "caracteres raros". Se normaliza la puntuación a su equivalente
    // ASCII y se descarta cualquier otro carácter fuera de ese rango.
    const sanitizarParaPDF = (texto) => String(texto ?? '')
        .normalize('NFC')
        .replace(/[‘’]/g, "'")
        .replace(/[“”]/g, '"')
        .replace(/[–—]/g, '-')
        .replace(/•/g, '-')
        .replace(/…/g, '...')
        .replace(/[^\x00-\xFF]/g, '');

    const markdownAPlano = (md) => sanitizarParaPDF((md || '')
        .replace(/^#{1,6}\s*/gm, '')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/[*_`~]/g, '')
        .replace(/^>\s?/gm, '')
        .replace(/^[-*]{3,}$/gm, '')
        .replace(/^[-*]\s+/gm, '- '));

    const exportarPDF = () => {
        if (selectedModels.length < 2) { toast.warning('Agrega al menos 2 modelos para exportar.'); return; }

        const doc = new jsPDF({ unit: 'pt', format: 'a4' });
        const marginX = 40;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const contentWidth = pageWidth - marginX * 2;
        let y = 40;
        const s = sanitizarParaPDF;

        const saltoDePaginaSiNecesario = (alturaNecesaria) => {
            if (y + alturaNecesaria > pageHeight - 40) { doc.addPage(); y = 40; }
        };
        const tituloSeccion = (texto) => {
            saltoDePaginaSiNecesario(28);
            doc.setFontSize(13); doc.setFont(undefined, 'bold'); doc.setTextColor(30, 30, 30);
            doc.text(s(texto), marginX, y);
            y += 18;
        };

        // Header
        doc.setFontSize(17); doc.setFont(undefined, 'bold'); doc.setTextColor(20, 20, 20);
        doc.text('Informe comparativo - DoctorCar', marginX, y);
        y += 20;
        doc.setFontSize(10); doc.setFont(undefined, 'normal'); doc.setTextColor(120, 120, 120);
        doc.text(s(`Generado el ${new Date().toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' })}`), marginX, y);
        y += 20;
        doc.setFontSize(11); doc.setTextColor(30, 30, 30);
        selectedModels.forEach(m => {
            doc.text(s(`-  ${m.marca} ${m.model} - ${m.modelDetail}`), marginX, y);
            y += 15;
        });
        y += 12;

        // Precios
        if (tiposPrecios.length) {
            tituloSeccion('Comparación de precios');
            autoTable(doc, {
                startY: y,
                head: [['Tipo de precio', ...selectedModels.map(m => s(`${m.marca} ${m.model}`))]],
                body: tiposPrecios.map(tipo => [
                    s(tipo),
                    ...selectedModels.map(m => {
                        const precio = (preciosMap[m.id] || []).find(r => r.tipo === tipo)?.precio || 0;
                        return precio ? s(clpFmt(precio)) : '-';
                    }),
                ]),
                styles: { fontSize: 9, cellPadding: 5 },
                headStyles: { fillColor: [22, 101, 52] },
                margin: { left: marginX, right: marginX },
            });
            y = doc.lastAutoTable.finalY + 24;
        }

        // Especificaciones técnicas
        if (specRows.length) {
            tituloSeccion('Especificaciones técnicas');
            const body = [];
            specRows.forEach(row => {
                if (row.type === 'section') {
                    body.push([{
                        content: s(row.label), colSpan: selectedModels.length + 1,
                        styles: { fontStyle: 'bold', fillColor: [249, 250, 251], textColor: [55, 65, 81] },
                    }]);
                } else {
                    body.push([s(row.label.replace(/_/g, ' ')), ...row.values.map(v => v ? s(v) : '-')]);
                }
            });
            autoTable(doc, {
                startY: y,
                head: [['Especificación', ...selectedModels.map(m => s(`${m.marca} ${m.model}`))]],
                body,
                styles: { fontSize: 8, cellPadding: 4 },
                headStyles: { fillColor: [146, 64, 14] },
                margin: { left: marginX, right: marginX },
            });
            y = doc.lastAutoTable.finalY + 24;
        }

        // Equipamiento
        if (equipRows.length) {
            tituloSeccion('Equipamiento');
            const body = [];
            equipRows.forEach(row => {
                if (row.type === 'equip-section') {
                    body.push([{
                        content: s(row.label), colSpan: selectedModels.length + 1,
                        styles: { fontStyle: 'bold', fillColor: [249, 250, 251], textColor: [55, 65, 81] },
                    }]);
                } else {
                    body.push([s(row.label), ...row.values.map(v => v === 'SI' ? 'Si' : v === 'NO' ? 'No' : '-')]);
                }
            });
            autoTable(doc, {
                startY: y,
                head: [['Característica', ...selectedModels.map(m => s(`${m.marca} ${m.model}`))]],
                body,
                styles: { fontSize: 8, cellPadding: 4 },
                headStyles: { fillColor: [124, 58, 237] },
                margin: { left: marginX, right: marginX },
            });
            y = doc.lastAutoTable.finalY + 24;
        }

        // Análisis IA
        if (aiResponse) {
            tituloSeccion('Análisis de Product Manager (IA)');
            doc.setFontSize(10); doc.setFont(undefined, 'normal'); doc.setTextColor(50, 50, 50);
            const lineas = doc.splitTextToSize(markdownAPlano(aiResponse), contentWidth);
            lineas.forEach(linea => {
                saltoDePaginaSiNecesario(14);
                doc.text(linea, marginX, y);
                y += 13;
            });
        }

        const nombreArchivo = `comparador_${selectedModels.map(m => m.model).join('_vs_').replace(/[^a-zA-Z0-9_-]/g, '')}_${new Date().toISOString().slice(0, 10)}.pdf`;
        doc.save(nombreArchivo);
        toast.success('PDF generado');
    };

    // ── Exportar informe en Excel ──────────────────────────────────
    const exportarExcel = () => {
        if (selectedModels.length < 2) { toast.warning('Agrega al menos 2 modelos para exportar.'); return; }

        const nombresModelos = selectedModels.map(m => `${m.marca} ${m.model}`);
        const wb = XLSX.utils.book_new();

        // Resumen
        const resumenData = selectedModels.map(m => ({
            Marca: m.marca, Modelo: m.model, Versión: m.modelDetail,
            'Precio de lista': (() => {
                const fila = (preciosMap[m.id] || []).find(r => /lista/i.test(r.tipo)) || (preciosMap[m.id] || [])[0];
                return fila ? fila.precio : '';
            })(),
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(resumenData), 'Resumen');

        // Precios
        if (tiposPrecios.length) {
            const preciosData = tiposPrecios.map(tipo => {
                const fila = { 'Tipo de precio': tipo };
                selectedModels.forEach((m, i) => {
                    const precio = (preciosMap[m.id] || []).find(r => r.tipo === tipo)?.precio || 0;
                    fila[nombresModelos[i]] = precio || '';
                });
                return fila;
            });
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(preciosData), 'Precios');
        }

        // Especificaciones técnicas
        if (specRows.length) {
            let seccionActual = '';
            const specsData = [];
            specRows.forEach(row => {
                if (row.type === 'section') { seccionActual = row.label.replace(/^\S+\s/, ''); return; }
                const fila = { 'Sección': seccionActual, 'Especificación': row.label.replace(/_/g, ' ') };
                selectedModels.forEach((m, i) => { fila[nombresModelos[i]] = row.values[i] || ''; });
                specsData.push(fila);
            });
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(specsData), 'Especificaciones');
        }

        // Equipamiento
        if (equipRows.length) {
            let categoriaActual = '';
            const equipData = [];
            equipRows.forEach(row => {
                if (row.type === 'equip-section') { categoriaActual = row.label.replace(/^\S+\s/, ''); return; }
                const fila = { Categoría: categoriaActual, Característica: row.label };
                selectedModels.forEach((m, i) => {
                    fila[nombresModelos[i]] = row.values[i] === 'SI' ? 'Si' : row.values[i] === 'NO' ? 'No' : '';
                });
                equipData.push(fila);
            });
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(equipData), 'Equipamiento');
        }

        // Análisis IA
        if (aiResponse) {
            const lineasIA = markdownAPlano(aiResponse).split('\n').filter(Boolean).map(linea => ({ 'Análisis de Product Manager (IA)': linea }));
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(lineasIA), 'Análisis IA');
        }

        const nombreArchivo = `comparador_${selectedModels.map(m => m.model).join('_vs_').replace(/[^a-zA-Z0-9_-]/g, '')}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(wb, nombreArchivo);
        toast.success('Excel generado');
    };

    // ── Render selector (compartido) ────────────────────────────
    const renderSelector = () => (
        <div className="comp-card">
            <div className="comp-card-header">
                <div className="comp-card-icon"><Search size={13} /></div>
                <span className="comp-card-title">Modelos en comparación</span>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>{selectedModels.length}/6</span>
                {selectedModels.length >= 2 && (
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                        <button className="comp-btn" onClick={exportarPDF}>
                            <FileDown size={13} /> Exportar PDF
                        </button>
                        <button className="comp-btn" onClick={exportarExcel}>
                            <FileDown size={13} /> Exportar Excel
                        </button>
                        <button className="comp-btn" onClick={() => setShowSaveModal(true)}>
                            <Save size={13} /> Guardar
                        </button>
                    </div>
                )}
            </div>
            <div className="comp-card-body">
                <div className="comp-search-row">
                    <div className="comp-search-wrap" ref={searchRef}>
                        <span className="comp-search-icon"><Search size={14} /></span>
                        <input
                            ref={inputRef} type="text" className="comp-search-input"
                            placeholder="Buscar por marca, modelo o versión…"
                            value={searchText} onChange={e => setSearchText(e.target.value)}
                            onFocus={calcPos} onClick={calcPos}
                            disabled={selectedModels.length >= 6}
                        />
                        {dropdownOpen && (
                            <div className="comp-dropdown" style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}>
                                {searchLoading && <div className="comp-dropdown-empty">Buscando…</div>}
                                {!searchLoading && !searchResults.length && <div className="comp-dropdown-empty">Sin resultados para "{searchText}"</div>}
                                {!searchLoading && searchResults.map(r => {
                                    const ya = !!selectedModels.find(m => m.id === r.id);
                                    return (
                                        <div key={r.id} className={`comp-dropdown-item ${ya ? 'disabled' : ''}`} onClick={() => !ya && agregar(r)}>
                                            <span className="comp-dropdown-marca">{r.marca}</span>
                                            <span className="comp-dropdown-nombre">{r.model}</span>
                                            <span className="comp-dropdown-version">{r.modelDetail}</span>
                                            {ya && <CheckCircle size={12} style={{ color: '#22c55e', flexShrink: 0 }} />}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
                {selectedModels.length > 0 ? (
                    <div className="comp-selected">
                        {selectedModels.map((m, i) => (
                            <div key={m.id} className="comp-selected-chip" style={{ borderColor: MODEL_COLORS[i % 6], color: MODEL_COLORS[i % 6] }}>
                                <span style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {m.marca} {m.model} {m.modelDetail}
                                </span>
                                <button className="comp-selected-chip-remove" onClick={() => quitar(m.id)} style={{ color: MODEL_COLORS[i % 6] }}>
                                    <X size={10} />
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="comp-min-hint">Busca y agrega al menos 2 modelos para comparar.</p>
                )}
            </div>
        </div>
    );

    // ── MODEL HEADER (shared) ───────────────────────────────────
    const modeloHeader = (m, i, extra = {}) => (
        <div key={m.id} style={{ padding: '8px 10px', textAlign: 'center', background: '#f9fafb', borderBottom: '2px solid #e5e7eb', borderLeft: '1px solid #e5e7eb', ...extra }}>
            <div className="comp-model-header" style={{ background: MODEL_COLORS[i % 6] }}>{m.marca} {m.model}</div>
            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3, lineHeight: 1.3 }}>{m.modelDetail}</div>
        </div>
    );

    // ── RENDER ──────────────────────────────────────────────────
    const innerContent = (
        <div className="comp-main">
            <div className="comp-wrapper">

                {/* Título — se omite en modo embebido (Mis Proyectos ya
                    muestra su propio encabezado de proyecto). */}
                {!embedded && (
                    <div className="comp-page-title">
                        <div className="comp-page-icon"><BarChart3 size={18} /></div>
                        <div>
                            <h1 className="comp-page-heading">Comparador Pro</h1>
                            <p className="comp-page-sub">Precios, specs, evolución histórica y análisis de Product Manager</p>
                        </div>
                    </div>
                )}

                {/* Tabs — ocultas en modo embebido (Mis Proyectos): ahí el
                    comparador solo muestra la tabla de comparación, sin
                    subpestañas propias que se confundan con las del
                    proyecto (Proyecto/Comparar/IA/Competencia). */}
                {!embedded && (
                    <div className="comp-tabs">
                        {TABS.map(t => (
                            <button key={t.key} className={`comp-tab-btn ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}>
                                {t.icon} {t.label}
                            </button>
                        ))}
                    </div>
                )}

                {/* Selector siempre visible */}
                {renderSelector()}

                {/* ═══ TAB: COMPARAR ═══════════════════════════════════ */}
                {activeTab === 'comparar' && selectedModels.length >= 2 && (
                    <>
                        {allLoading && <div className="comp-loading"><div className="comp-spinner" /> Cargando datos…</div>}

                        {/* Tabla precios */}
                        <div className="comp-card">
                            <div className="comp-card-header">
                                <div className="comp-card-icon" style={{ background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' }}>💰</div>
                                <span className="comp-card-title">Comparación de precios</span>
                            </div>
                            <div className="comp-table-wrap">
                                <div style={{ display: 'grid', gridTemplateColumns: `200px repeat(${selectedModels.length},1fr)`, minWidth: 400 + selectedModels.length * 160 }}>
                                    <div style={{ padding: '10px 16px', background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }} />
                                    {selectedModels.map((m, i) => modeloHeader(m, i))}
                                    {!tiposPrecios.length && !allLoading && (
                                        <div style={{ gridColumn: '1/-1', padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                                            No se encontraron precios.
                                        </div>
                                    )}
                                    {tiposPrecios.map((tipo, rowIdx) => {
                                        const valores = selectedModels.map(m => (preciosMap[m.id] || []).find(r => r.tipo === tipo)?.precio || 0);
                                        const validos = valores.filter(v => v > 0);
                                        const minP = validos.length ? Math.min(...validos) : 0;
                                        const isLast = rowIdx === tiposPrecios.length - 1;
                                        const bg = rowIdx % 2 === 0 ? '#fff' : '#fafafa';
                                        return (
                                            <React.Fragment key={tipo}>
                                                <div style={{ padding: '11px 16px', background: bg, borderBottom: isLast ? 'none' : '1px solid #f3f4f6', display: 'flex', alignItems: 'center', fontSize: 13, color: '#374151' }}>{tipo}</div>
                                                {selectedModels.map((m, i) => {
                                                    const precio = valores[i];
                                                    const isBest = precio > 0 && precio === minP && validos.length > 1;
                                                    return (
                                                        <div key={m.id} style={{ padding: '11px 16px', background: isBest ? '#f0fdf4' : bg, borderBottom: isLast ? 'none' : '1px solid #f3f4f6', borderLeft: `2px solid ${MODEL_COLORS[i % 6]}30`, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, fontVariantNumeric: 'tabular-nums', fontWeight: precio > 0 ? 600 : 400, fontSize: 13, color: precio === 0 ? '#d1d5db' : isBest ? '#166534' : '#111827', whiteSpace: 'nowrap' }}>
                                                            {precio === 0 ? '—' : clpFmt(precio)}
                                                            {isBest && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 999, background: '#dcfce7', color: '#166534', fontWeight: 700 }}>más bajo</span>}
                                                        </div>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Specs */}
                        <div className="comp-card">
                            <div className="comp-card-header">
                                <div className="comp-card-icon" style={{ background: '#fef3c7', borderColor: '#fde68a', color: '#92400e' }}>⚙️</div>
                                <span className="comp-card-title">Especificaciones técnicas</span>
                                {selectedModels.some(m => specsLoading[m.id]) && <div className="comp-spinner" style={{ marginLeft: 'auto' }} />}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: `180px repeat(${selectedModels.length},1fr)`, borderBottom: '2px solid #e5e7eb', background: '#f9fafb' }}>
                                <div style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Especificación</div>
                                {selectedModels.map((m, i) => (
                                    <div key={m.id} style={{ padding: '8px 10px', textAlign: 'center', background: '#f9fafb', borderLeft: '1px solid #e5e7eb' }}>
                                        <div className="comp-model-header" style={{ background: MODEL_COLORS[i % 6] }}>{m.marca} {m.model}</div>
                                        <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3 }}>{m.modelDetail}</div>
                                        {specsMap[m.id] === null && !specsLoading[m.id] && <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 3 }}>sin ficha</div>}
                                    </div>
                                ))}
                            </div>
                            {!specRows.length && !allLoading ? (
                                <div className="comp-state"><AlertCircle size={24} style={{ opacity: .3 }} /><p>No hay specs disponibles.</p></div>
                            ) : specRows.map((row, idx) => row.type === 'section' ? (
                                <div key={idx} className="comp-specs-section-title">{row.label}</div>
                            ) : (
                                <div key={idx} className={`comp-spec-row ${row.hasDiff ? 'highlight' : ''}`} style={{ display: 'grid', gridTemplateColumns: `180px repeat(${selectedModels.length},1fr)` }}>
                                    <div className="comp-spec-label">{row.label.replace(/_/g, ' ')}</div>
                                    {row.values.map((val, i) => <div key={i} className={`comp-spec-value ${!val ? 'empty' : ''}`} style={val ? { borderLeft: `2px solid ${MODEL_COLORS[i % 6]}20` } : {}}>{val || '—'}</div>)}
                                </div>
                            ))}
                        </div>

                        {/* Equipamiento */}
                        {equipRows.length > 0 && (
                            <div className="comp-card">
                                <div className="comp-card-header">
                                    <div className="comp-card-icon" style={{ background: '#f5f3ff', borderColor: '#ddd6fe', color: '#7c3aed' }}>✨</div>
                                    <span className="comp-card-title">Equipamiento</span>
                                    <span style={{ marginLeft: 'auto', fontSize: 12, color: '#9ca3af' }}>Amarillo = diferencias</span>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: `220px repeat(${selectedModels.length},1fr)`, borderBottom: '2px solid #e5e7eb', background: '#f9fafb' }}>
                                    <div style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Característica</div>
                                    {selectedModels.map((m, i) => (
                                        <div key={m.id} style={{ padding: '8px 10px', textAlign: 'center', borderLeft: '1px solid #e5e7eb' }}>
                                            <div className="comp-model-header" style={{ background: MODEL_COLORS[i % 6] }}>{m.marca} {m.model}</div>
                                            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3 }}>{m.modelDetail}</div>
                                        </div>
                                    ))}
                                </div>
                                {equipRows.map((row, idx) => row.type === 'equip-section' ? (
                                    <div key={idx} className="comp-specs-section-title">{row.label}</div>
                                ) : (
                                    <div key={idx} className={`comp-spec-row ${row.hasDiff ? 'highlight' : ''}`} style={{ display: 'grid', gridTemplateColumns: `220px repeat(${selectedModels.length},1fr)` }}>
                                        <div className="comp-spec-label" style={{ fontSize: 11 }}>{row.label}</div>
                                        {row.values.map((val, i) => (
                                            <div key={i} className="comp-spec-value" style={{ justifyContent: 'center', borderLeft: `2px solid ${MODEL_COLORS[i % 6]}25` }}>
                                                {val === null ? <span style={{ color: '#e5e7eb', fontSize: 12 }}>—</span>
                                                    : val === 'SI' ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#166634', fontWeight: 600, fontSize: 12 }}><svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 6.5L5 9.5L11 3.5" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>Sí</span>
                                                        : val === 'NO' ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#d1d5db', fontSize: 12 }}><svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M1 1L10 10M10 1L1 10" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" /></svg>No</span>
                                                            : <span style={{ fontSize: 12, color: '#374151' }}>{val}</span>}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {/* ═══ TAB: EVOLUCIÓN ══════════════════════════════════ */}
                {!embedded && activeTab === 'evolucion' && (
                    <div className="comp-card">
                        <div className="comp-card-header">
                            <div className="comp-card-icon" style={{ background: '#eff6ff', borderColor: '#bfdbfe', color: '#2563eb' }}><TrendingUp size={13} /></div>
                            <span className="comp-card-title">Evolución histórica de precios</span>
                            {selectedModels.some(m => histLoading[m.id]) && <div className="comp-spinner" style={{ marginLeft: 'auto' }} />}
                        </div>
                        {selectedModels.length < 2 ? (
                            <div className="comp-state"><TrendingUp size={24} style={{ opacity: .2 }} /><p>Agrega al menos 2 modelos para ver la evolución.</p></div>
                        ) : (
                            <div className="comp-card-body">
                                {/* Controles */}
                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <div>
                                        <span style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>Modo</span>
                                        <div className="comp-chart-tabs">
                                            {[['absoluto', 'CLP'], ['relativo', '% relativo']].map(([v, l]) => (
                                                <button key={v} className={`comp-chart-tab ${chartMode === v ? 'active' : ''}`} onClick={() => setChartMode(v)}>{l}</button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <span style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>Tipo de precio</span>
                                        <div className="comp-chart-tabs">
                                            <button className={`comp-chart-tab ${chartTipo === 'todos' ? 'active' : ''}`} onClick={() => setChartTipo('todos')}>Todos</button>
                                            {todosLosTipos.map(t => (
                                                <button key={t} className={`comp-chart-tab ${chartTipo === t ? 'active' : ''}`} onClick={() => setChartTipo(t)}>
                                                    {t.length > 18 ? t.slice(0, 17) + '…' : t}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {!chartData.length ? (
                                    <div className="comp-state" style={{ padding: 32 }}><p>Sin datos históricos suficientes para graficar.</p></div>
                                ) : (
                                    <div className="comp-chart-wrap">
                                        <ResponsiveContainer width="100%" height={320}>
                                            <LineChart data={chartData} margin={{ top: 8, right: 20, left: chartMode === 'relativo' ? 40 : 80, bottom: 8 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,.1)" />
                                                <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                                                <YAxis
                                                    tickFormatter={v => chartMode === 'relativo' ? `${v > 0 ? '+' : ''}${v}%` : `$${(v / 1_000_000).toFixed(1)}M`}
                                                    width={chartMode === 'relativo' ? 48 : 80} tick={{ fontSize: 11 }}
                                                />
                                                <Tooltip formatter={(v, name) => chartMode === 'relativo' ? [`${v > 0 ? '+' : ''}${v}%`, name] : [clpFmt(v), name]} />
                                                <Legend verticalAlign="top" align="right" wrapperStyle={{ fontSize: 12 }} />
                                                {selectedModels.map((m, i) => (
                                                    <Line key={m.id} type="monotone" dataKey={`${m.marca} ${m.model}`} stroke={MODEL_COLORS[i % 6]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                                                ))}
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}

                                {/* Leyenda de modelos */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {selectedModels.map((m, i) => (
                                        <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                                            <div style={{ width: 12, height: 3, borderRadius: 2, background: MODEL_COLORS[i % 6] }} />
                                            <span style={{ color: '#374151' }}>{m.marca} {m.model}</span>
                                            <span style={{ color: '#9ca3af' }}>{m.modelDetail}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ═══ TAB: IA PM ══════════════════════════════════════ */}
                {!embedded && activeTab === 'ia' && (
                    <div className="comp-card">
                        <div className="comp-card-header">
                            <div className="comp-card-icon" style={{ background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' }}><Bot size={13} /></div>
                            <span className="comp-card-title">Análisis Product Manager</span>
                            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                                {!aiResponse && selectedModels.length >= 1 && (
                                    <button className="comp-btn comp-btn--primary" style={{ fontSize: 12 }} onClick={() => analizarConIA()} disabled={aiLoading}>
                                        <Bot size={13} /> Análisis completo
                                    </button>
                                )}
                                {aiHistory.length > 0 && (
                                    <button className="comp-btn" style={{ fontSize: 12 }} onClick={() => { setAiHistory([]); setAiResponse(''); }}>
                                        <X size={12} /> Nueva sesión
                                    </button>
                                )}
                            </div>
                        </div>

                        {selectedModels.length < 1 ? (
                            <div className="comp-state"><Bot size={24} style={{ opacity: .2 }} /><p>Agrega al menos 1 modelo para analizar.</p></div>
                        ) : (
                            <div className="comp-card-body" style={{ gap: 0, padding: 0 }}>

                                {/* Modelos en análisis */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '14px 18px', borderBottom: '1px solid #f3f4f6' }}>
                                    {selectedModels.map((m, i) => (
                                        <span key={m.id} style={{ background: MODEL_COLORS[i % 6] + '18', color: MODEL_COLORS[i % 6], border: `1px solid ${MODEL_COLORS[i % 6]}40`, borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>
                                            {m.marca} {m.model}
                                        </span>
                                    ))}
                                </div>

                                {/* Hilo de conversación */}
                                {aiHistory.length > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, maxHeight: 560, overflowY: 'auto', padding: '12px 18px' }}>
                                        {aiHistory.map((msg, idx) => (
                                            <div key={idx} style={{
                                                marginBottom: 12,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                            }}>
                                                {/* Etiqueta */}
                                                <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                                                    {msg.role === 'user' ? 'PM' : 'Análisis IA'}
                                                </div>

                                                {msg.role === 'user' ? (
                                                    /* Burbuja usuario */
                                                    <div style={{ background: '#2563eb', color: '#fff', borderRadius: '12px 12px 3px 12px', padding: '10px 14px', maxWidth: '75%', fontSize: 13, lineHeight: 1.5 }}>
                                                        {msg.content}
                                                    </div>
                                                ) : (
                                                    /* Respuesta IA en card oscura */
                                                    <div style={{ width: '100%' }}>
                                                        <AIResponse text={msg.content} />
                                                    </div>
                                                )}
                                            </div>
                                        ))}

                                        {/* Loading en hilo */}
                                        {aiLoading && (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: 12 }}>
                                                <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>Análisis IA</div>
                                                <div className="comp-ai-thinking" style={{ background: '#0f172a', borderRadius: 12, padding: '12px 16px' }}>
                                                    <div className="comp-ai-dot" /><div className="comp-ai-dot" /><div className="comp-ai-dot" />
                                                    <span style={{ color: '#94a3b8', fontSize: 13 }}>Analizando desde perspectiva PM…</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Estado vacío */}
                                {!aiHistory.length && !aiLoading && (
                                    <div className="comp-state" style={{ padding: '32px 24px' }}>
                                        <Bot size={28} style={{ opacity: .2 }} />
                                        <p>Haz click en "Análisis completo" o escribe una consulta específica abajo.</p>
                                    </div>
                                )}

                                {/* Input de seguimiento — siempre visible */}
                                <div style={{ borderTop: '1px solid #f3f4f6', padding: '12px 18px', display: 'flex', gap: 8, alignItems: 'flex-end', background: '#fafafa' }}>
                                    <textarea
                                        style={{ flex: 1, padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'none', minHeight: 56, maxHeight: 120, color: '#111827', background: '#fff', lineHeight: 1.5, outline: 'none', transition: 'border-color .15s' }}
                                        placeholder={aiHistory.length
                                            ? 'Escribe un comentario o pregunta de seguimiento…'
                                            : `Ej: ¿Cuál de estos modelos tiene mejor ratio precio/equipamiento?`}
                                        value={aiPrompt}
                                        onChange={e => setAiPrompt(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && aiPrompt.trim()) {
                                                e.preventDefault();
                                                analizarConIA(aiPrompt);
                                            }
                                        }}
                                        onFocus={e => { e.target.style.borderColor = '#2563eb'; }}
                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; }}
                                    />
                                    <button
                                        className="comp-btn comp-btn--primary"
                                        onClick={() => analizarConIA(aiPrompt)}
                                        disabled={aiLoading || !aiPrompt.trim()}
                                        style={{ padding: '10px 16px', alignSelf: 'flex-end', flexShrink: 0 }}
                                    >
                                        <Bot size={14} /> {aiLoading ? '…' : 'Enviar'}
                                    </button>
                                </div>
                                <div style={{ padding: '4px 18px 10px', fontSize: 11, color: '#d1d5db' }}>
                                    Cmd/Ctrl + Enter para enviar
                                </div>

                            </div>
                        )}
                    </div>
                )}

                {/* ═══ TAB: GUARDADAS ══════════════════════════════════ */}
                {!embedded && activeTab === 'guardadas' && (
                    <div className="comp-card">
                        <div className="comp-card-header">
                            <div className="comp-card-icon" style={{ background: '#fef3c7', borderColor: '#fde68a', color: '#92400e' }}><BookOpen size={13} /></div>
                            <span className="comp-card-title">Comparaciones guardadas</span>
                            <button className="comp-btn" style={{ marginLeft: 'auto', fontSize: 11 }} onClick={cargarGuardadas} disabled={savedLoading}>
                                ↺ Recargar
                            </button>
                        </div>
                        <div className="comp-card-body">
                            {savedLoading && <div className="comp-loading"><div className="comp-spinner" /> Cargando…</div>}
                            {!savedLoading && !savedList.length && (
                                <div className="comp-state">
                                    <BookOpen size={24} style={{ opacity: .2 }} />
                                    <p>No tienes comparaciones guardadas todavía.</p>
                                    <p style={{ fontSize: 12 }}>Arma una comparación y haz click en "Guardar".</p>
                                </div>
                            )}
                            {!savedLoading && savedList.length > 0 && (
                                <div className="comp-saved-list">
                                    {savedList.map(comp => (
                                        <div key={comp.id} className="comp-saved-item">
                                            <div className="comp-saved-info">
                                                <div className="comp-saved-name">{comp.nombre}</div>
                                                <div className="comp-saved-models">
                                                    {(comp.modelos || []).map(m => `${m.marca} ${m.model}`).join(' · ')}
                                                </div>
                                            </div>
                                            <div className="comp-saved-date">
                                                {comp.createdAt ? new Date(comp.createdAt).toLocaleDateString('es-CL') : '—'}
                                            </div>
                                            <div className="comp-saved-actions">
                                                <button className="comp-btn" style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => cargarGuardadaEnComparador(comp)}>
                                                    <Plus size={12} /> Cargar
                                                </button>
                                                <button className="comp-btn comp-btn--danger" style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => borrarGuardada(comp.id)}>
                                                    <Trash2 size={12} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Modal guardar ── */}
                {showSaveModal && (
                    <div className="comp-modal-overlay" onClick={() => setShowSaveModal(false)}>
                        <div className="comp-modal" onClick={e => e.stopPropagation()}>
                            <div style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>Guardar comparación</div>
                            <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
                                {selectedModels.map(m => `${m.marca} ${m.model}`).join(' · ')}
                            </p>
                            <input
                                type="text" className="comp-modal-input"
                                placeholder={`Comparación ${new Date().toLocaleDateString('es-CL')}`}
                                value={saveName} onChange={e => setSaveName(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && guardarComparacion()}
                                autoFocus
                            />
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                <button className="comp-btn" onClick={() => setShowSaveModal(false)}>Cancelar</button>
                                <button className="comp-btn comp-btn--primary" onClick={guardarComparacion} disabled={saving}>
                                    <Save size={13} /> {saving ? 'Guardando…' : 'Guardar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    if (embedded) return innerContent;
    return (
        <div className="layout">
            <Sidebar />
            {innerContent}
        </div>
    );
}

export default ComparadorModelos;